import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { TrendingUp, Users, ArrowRight, CheckCircle, Zap } from 'lucide-react';
import { solutions } from '../data/mockData';
import {
  SolutionIcon1, SolutionIcon2, SolutionIcon3, SolutionIcon4, SolutionIcon5, SolutionIcon6, SolutionIcon7, SolutionIcon8, SolutionIcon9
} from '../components/Icons/Icons'

const SolutionsPage = () => {
  const getIcon = (iconName) => {
    const icons = { SolutionIcon1, SolutionIcon2, SolutionIcon3, SolutionIcon4, SolutionIcon5, SolutionIcon6, SolutionIcon7, SolutionIcon8, SolutionIcon9 };
    return icons[iconName] || SolutionIcon1;
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="container relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-in">
            <h1 className="display-lg text-white mb-6">
              Practical Answers to Common Sales and Service Problems
            </h1>
            <p className="body-lg mb-8">
              Slow follow-up, missed leads, manual admin and unreliable forecasts are the problems we hear about most. GrowSquare addresses each of them with workflows your team can start using in the first weeks.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-start items-start sm:items-center">
              <Link to="/demo">
                <Button className="btn-primary glow-effect">
                  Request Demo
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button className="btn-secondary">
                  Speak with Expert
                </Button>
              </Link>
            </div>
          </div>
          <div className="relative flex justify-center">
            <img
              src='/images/solutionsBanner.webp'
              alt="Smiling sales professional holding a tablet while a colleague looks on"
              loading="eager"
              className="w-full max-w-[420px] aspect-[4/5] object-cover object-[50%_25%] rounded-xl border border-slate-700 shadow-2xl"
            />
          </div>
          </div>
        </div>
      </section>

      {/* Solutions Overview */}
      <section className="section-padding">
        <div className="container">
          <div className="text-center mb-16">
            <Badge className="bg-green-400/10 text-green-400 mb-4">Customer-Focused Solutions</Badge>
            <h2 className="display-md text-white mb-6">
              Start with the Problem You Have
            </h2>
            <p className="body-lg max-w-3xl mx-auto">
              Each solution is built from the GrowSquare modules that address one business problem, so you can start with one and add others later.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {solutions.map((solution, i) => {
              const IconComponent = getIcon(solution.icon);

              return (
                <Card key={i} className="feature-card hover-card">
                  <CardHeader>
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-12 h-12 rounded-lg bg-green-400/10 flex items-center justify-center">
                        <IconComponent className="h-6 w-6 text-green-400" />
                      </div>
                      {/* <Badge className="bg-green-400/10 text-green-400">
                        {solution.value}
                      </Badge> */}
                    </div>
                    <CardTitle className="h2 text-white">{solution.title}</CardTitle>
                    <CardDescription className="body-md">
                      {solution.subTitle}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3 mb-6">
                      {solution.features.map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex items-start space-x-2">
                          <CheckCircle className="h-4 w-4 text-green-400 mt-1 flex-shrink-0" />
                          <span className="body-sm text-slate-300">{feature}</span>
                        </div>
                      ))}
                    </div>
                    <Link to={`/solutions/${solution.title}`}>
                      <Button className="btn-secondary w-full">
                        Learn More
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section >

      {/* Value Proposition */}
      < section className="section-padding section-dark" >
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="display-md text-white mb-6">
                What Teams Typically See
              </h2>
              <p className="body-lg mb-8">
                Most gains come from three places: less time spent on admin, faster follow-up on new leads, and earlier warning when an account is at risk.
              </p>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 rounded-lg bg-green-400/10 flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="h-5 w-5 text-green-400" />
                  </div>
                  <div>
                    <h3 className="h3 text-white mb-2">Time Saved on Admin</h3>
                    <p className="body-md text-slate-300">
                      Automatic lead assignment, activity logging and email drafting typically give reps back a few hours each week.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 rounded-lg bg-green-400/10 flex items-center justify-center flex-shrink-0">
                    <Zap className="h-5 w-5 text-green-400" />
                  </div>
                  <div>
                    <h3 className="h3 text-white mb-2">Tighter Sales Cycles</h3>
                    <p className="body-md text-slate-300">
                      Clear stages, next-step reminders and approval rules reduce the time deals spend waiting on someone.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 rounded-lg bg-green-400/10 flex items-center justify-center flex-shrink-0">
                    <Users className="h-5 w-5 text-green-400" />
                  </div>
                  <div>
                    <h3 className="h3 text-white mb-2">Better Retention</h3>
                    <p className="body-md text-slate-300">
                      Tracking cases and renewals in one place helps account managers act before a customer leaves.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-square bg-gradient-to-br from-green-400/20 to-transparent rounded-2xl p-8">
                <div className="h-full bg-slate-800 rounded-xl p-8 flex flex-col justify-center">
                  <div className="text-center space-y-8">
                    <div>
                      <div className="text-6xl font-bold text-green-400 mb-2">3–5 hrs</div>
                      <p className="body-md">Saved per rep each week on admin</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-center">
                      <div>
                        <div className="h2 text-green-400">10–15%</div>
                        <p className="body-sm">Shorter sales cycles for teams using defined stages</p>
                      </div>
                      <div>
                        <div className="h2 text-green-400">5–10%</div>
                        <p className="body-sm">Improvement in retention where cases and renewals are tracked</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section >

      {/* Implementation Process */}
      < section className="section-padding" >
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="display-md text-white mb-6">
              How We Implement
            </h2>
            <p className="body-lg max-w-2xl mx-auto">
              We work in short steps so your team keeps selling while the system is set up.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-400/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-green-400">1</span>
              </div>
              <h3 className="h3 text-white mb-2">Assessment</h3>
              <p className="body-sm text-slate-400">
                We review your sales process, data and current tools, and agree what to set up first.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-green-400/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-green-400">2</span>
              </div>
              <h3 className="h3 text-white mb-2">Configuration</h3>
              <p className="body-sm text-slate-400">
                We set up modules, fields, stages and rules to match your process, and load your data.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-green-400/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-green-400">3</span>
              </div>
              <h3 className="h3 text-white mb-2">Training</h3>
              <p className="body-sm text-slate-400">
                We train each role on the screens they use every day, with short sessions and quick-reference guides.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-green-400/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-green-400">4</span>
              </div>
              <h3 className="h3 text-white mb-2">Review</h3>
              <p className="body-sm text-slate-400">
                After go-live we check usage and reports with you and adjust what is not working.
              </p>
            </div>
          </div>
        </div>
      </section >

      {/* Success Stories Preview */}
      {/* < section className="section-padding section-dark" >
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="display-md text-white mb-6">
              Success Stories
            </h2>
            <p className="body-lg max-w-2xl mx-auto">
              See how companies across industries have transformed their sales operations with our solutions.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="feature-card">
              <CardContent className="pt-6">
                <div className="mb-4">
                  <div className="h2 text-green-400">3x</div>
                  <p className="body-sm text-slate-400">Faster Deal Closure</p>
                </div>
                <p className="body-md mb-4">
                  "GrowSquare's AI lead intelligence transformed our prospecting process. We're closing deals 3x faster with better qualified leads."
                </p>
                <p className="body-sm text-slate-300 font-medium">TechFlow Solutions</p>
                <p className="body-sm text-slate-400">SaaS Company</p>
              </CardContent>
            </Card>

            <Card className="feature-card">
              <CardContent className="pt-6">
                <div className="mb-4">
                  <div className="h2 text-green-400">40%</div>
                  <p className="body-sm text-slate-400">Higher Retention Rate</p>
                </div>
                <p className="body-md mb-4">
                  "The automation rules and customer health scoring helped us proactively prevent churn and increase customer lifetime value."
                </p>
                <p className="body-sm text-slate-300 font-medium">FinanceFirst</p>
                <p className="body-sm text-slate-400">Financial Services</p>
              </CardContent>
            </Card>

            <Card className="feature-card">
              <CardContent className="pt-6">
                <div className="mb-4">
                  <div className="h2 text-green-400">95%</div>
                  <p className="body-sm text-slate-400">Forecast Accuracy</p>
                </div>
                <p className="body-md mb-4">
                  "Predictive forecasting gave us the confidence to scale our team. Our revenue predictions are consistently accurate."
                </p>
                <p className="body-sm text-slate-300 font-medium">HealthTech Innovations</p>
                <p className="body-sm text-slate-400">Healthcare Technology</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section > */}

      {/* CTA Section */}
      {/* < section className="section-padding" >
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="display-md text-white mb-6">
              Ready to Transform Your Business?
            </h2>
            <p className="body-lg mb-8 max-w-2xl mx-auto">
              Don't let manual processes and outdated systems hold you back. Start your journey to
              sales excellence with GrowSquare's intelligent solutions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/demo">
                <Button className="btn-primary glow-effect">
                  Get Started Today
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button className="btn-secondary">
                  Speak with Expert
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section > */}
    </div >
  );
};
export default SolutionsPage;